import { Component, h, Prop, Element, Watch, State, Method } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';

enum TransitionStatus {
  COLLAPSED = 'COLLAPSED',
  COLLAPSING = 'COLLAPSING',
  EXPANDED = 'EXPANDED',
  EXPANDING = 'EXPANDING',
}

/**
 * COMPNAME is a reusable wrapper component used to expand and collapse content, as in an accordion. It should be used inside other components that need to reveal/hide content with an animated transition.
 *
 * The component accepts one prop ('expanded') that controls its expanded/collapsed state.
 */
@Component({
  tag: 'aafp-collapsible',
  shadow: false,
})
export class AafpCollapsible {
  @Element() element: HTMLElement;
  /**
   * Track state of this collapsible (open or closed)
   */
  @Prop() expanded: boolean = false;

  @State() transitionStatus: TransitionStatus = TransitionStatus.COLLAPSED;

  private contentElement: HTMLElement;
  private currentRaf: number;

  @Watch('expanded')
  watchExpandedHandler(newValue: boolean) {
    if (newValue) {
      this.expand(true);
    } else {
      this.collapse(true);
    }
  }

  componentWillLoad() {
    if (this.expanded) this.transitionStatus = TransitionStatus.EXPANDED;
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.currentRaf);
  }

  @Method()
  async toggle(shouldAnimate?: boolean) {
    if (this.transitionStatus === TransitionStatus.EXPANDING || this.transitionStatus === TransitionStatus.EXPANDED) {
      this.collapse(shouldAnimate);
    } else {
      this.expand(shouldAnimate);
    }
  }

  @Method()
  async expand(shouldAnimate?: boolean) {
    window.cancelAnimationFrame(this.currentRaf);

    if (shouldAnimate) {
      this.animateExpand();
    } else {
      this.contentElement.style.height = '';
      this.transitionStatus = TransitionStatus.EXPANDED;
    }
  }

  @Method()
  async collapse(shouldAnimate?: boolean) {
    window.cancelAnimationFrame(this.currentRaf);

    if (shouldAnimate) {
      this.animateCollapse();
    } else {
      this.contentElement.style.height = '';
      this.transitionStatus = TransitionStatus.COLLAPSED;
    }
  }

  private animateExpand() {
    // measure the current height of the content
    const currentHeight = parseInt(window.getComputedStyle(this.contentElement).getPropertyValue('height'), 10);

    if (currentHeight === 0) {
      this.contentElement.style.height = '';
      this.transitionStatus = TransitionStatus.EXPANDED;
      return;
    }

    // set the current height as inline style
    this.contentElement.style.height = `${currentHeight}px`;

    // update state to expanding, applies css classes
    this.transitionStatus = TransitionStatus.EXPANDING;

    // wait a tick
    this.currentRaf = window.requestAnimationFrame(() => {
      // measure the height the content would be when fully expanded
      const scrollHeight = this.contentElement.scrollHeight;

      // define a callback for when the component is completely expanded
      const onTransitionEnd = (e?: Event) => {
        if (e && e.target !== this.contentElement) {
          return;
        }

        this.contentElement.removeEventListener('transitionend', onTransitionEnd, false);

        if (this.transitionStatus !== TransitionStatus.EXPANDING) {
          return;
        }

        // update state to expanded, clear out inline style for the height
        this.transitionStatus = TransitionStatus.EXPANDED;
        this.contentElement.style.height = '';
      };

      if (currentHeight >= scrollHeight) {
        onTransitionEnd();
        return;
      }

      // set the height to its final value to kick off the css transition
      this.contentElement.addEventListener('transitionend', onTransitionEnd, false);
      this.contentElement.style.height = `${scrollHeight}px`;
    });
  }

  private animateCollapse() {
    // measure the current height of the content
    const currentHeight = parseInt(window.getComputedStyle(this.contentElement).getPropertyValue('height'), 10);

    // set the current height as inline style
    this.contentElement.style.height = `${currentHeight}px`;

    // update state to collapsing, applies css classes
    this.transitionStatus = TransitionStatus.COLLAPSING;

    // wait a tick
    this.currentRaf = window.requestAnimationFrame(() => {
      // define a callback for when the component is completely collapsed
      const onTransitionEnd = (e?: Event) => {
        if (e && e.target !== this.contentElement) {
          return;
        }

        this.contentElement.removeEventListener('transitionend', onTransitionEnd, false);

        if (this.transitionStatus !== TransitionStatus.COLLAPSING) {
          return;
        }

        // update state to collapsed, clear out inline style for the height
        this.transitionStatus = TransitionStatus.COLLAPSED;
        this.contentElement.style.height = '';
      };

      // set height to only the height of the first item to kick off the css transition
      let targetHeight = 0;

      if (currentHeight <= targetHeight) {
        onTransitionEnd();
        return;
      }

      this.contentElement.addEventListener('transitionend', onTransitionEnd, false);
      this.contentElement.style.height = `${targetHeight}px`;
    });
  }

  render() {
    return (
      <div
        aria-hidden={!this.expanded ? 'true' : 'false'}
        class={getClassListString(
          {
            'adl-collapsible--expanded': this.transitionStatus === TransitionStatus.EXPANDED,
            'adl-collapsible--expanding': this.transitionStatus === TransitionStatus.EXPANDING,
            'adl-collapsible--collapsed': this.transitionStatus === TransitionStatus.COLLAPSED,
            'adl-collapsible--collapsing': this.transitionStatus === TransitionStatus.COLLAPSING,
          },
          'adl-collapsible',
        )}
      >
        <div class="adl-collapsible__slot" ref={(el) => (this.contentElement = el as HTMLElement)}>
          <slot />
        </div>
      </div>
    );
  }
}
